import { axios } from '@/plugins/axios';
import merge from 'lodash/merge';
import Pager from '@/helpers/Pager';
import { Option, Pagination, Resource, Statement } from '@/helpers/Interfaces';
import { DateTime } from 'luxon';
import { $t } from '@/plugins/localization';
import { AxiosResponse } from 'axios';
import { BrandsTypes } from '../types/index';
import fileNameConverter from '../../common/helpers/fileNameConverter';

/**
 * PropertiesService
 */
export default class PropertiesService
{
    /**
     * @param filter FilterModel
     * @param pager Pager
     *
     * @returns Promise<Pagination<Resource<ListItemModel>>>
     */
    public static async getList(filter: FilterModel, pager: Pager): Promise<Pagination<Resource<ListItemModel>>>
    {
        return (await axios.get<Pagination<Resource<ListItemModel>>>('admin/renters/properties', {
            params: merge({}, filter, pager.data())
        })).data;
    }

    /**
     * @param search string
     * @param limit number
     * @param allRecordsVisible boolean
     * @returns Promise<ListItemModel[]>
     */
    public static async getAutocompleteList(search: string, limit: number = 5, allRecordsVisible: boolean = true): Promise<ListItemModel[]>
    {
        const encodedSearchValue = encodeURIComponent(search);

        return (await axios.get<ListItemModel[]>('admin/renters/properties/autocomplete', {
            params: {
                search: encodedSearchValue,
                limit: limit,
                allRecordsVisible
            }
        })).data;
    }

    /**
     * @param search string
     * @param limit number
     * @returns Promise<ListItemModel[]>
     */
    public static async getAutocompleteListV2(search: string, limit: number = 5): Promise<ListItemModel[]>
    {
        const encodedSearchValue = encodeURIComponent(search);

        return (await axios.get<ListItemModel[]>('admin/renters/properties/autocomplete/v2', {
            params: {
                search: encodedSearchValue,
                limit: limit
            }
        })).data;
    }

    /**
     * @param publicId string
     *
     * @returns Promise<ListItemModel>
     */
    public static async fetchAutocompleteModel(publicId: string): Promise<ListItemModel>
    {
        return (await axios.get<ListItemModel>(`admin/renters/properties/autocomplete/${publicId}`)).data;
    }

    /**
     * @param publicId string
     *
     * @returns Promise<ListItemModel>
     */
    public static async fetchAutocompleteModelV2(publicId: string): Promise<ListItemModel>
    {
        return (await axios.get<ListItemModel>(`admin/renters/properties/autocomplete/v2/${publicId}`)).data;
    }

    public static async getAutocompleteCustomList(search: string, limit: number = 5): Promise<ListItemModel[]>
    {
        return (await axios.get<ListItemModel[]>('admin/renters/customProperties/autocomplete', {
            params: {
                search: search,
                limit: limit,
            }
        })).data;
    }

    public static async fetchAutocompleteCustomModel(publicId: string): Promise<ListItemModel>
    {
        return (await axios.get<ListItemModel>(`admin/renters/customProperties/autocomplete/${publicId}`)).data;
    }

    /**
     * @param publicId string
     *
     * @returns Promise<Resource<FormModel>>
     */
    public static async fetch(publicId: string): Promise<Resource<FormModel>>
    {
        return (await axios.get<Resource<FormModel>>(`admin/renters/properties/${publicId}`)).data;
    }

    public static async fetchCustomProperty(customPublicId: string): Promise<Resource<FormModel>>
    {
        return (await axios.get<Resource<FormModel>>(`admin/renters/customProperties/${customPublicId}`)).data;
    }

    /**
     * @param model FormModel
     *
     * @returns Promise<Resource<FormModel>>
     */
    public static async create(model: FormModel): Promise<Resource<FormModel>>
    {
        return (await axios.post<Resource<FormModel>>(`admin/renters/properties`, model)).data;
    }

    /**
     * @param publicId string
     * @param model FormModel
     *
     * @returns Promise<Statement>
     */
    public static async update(publicId: string, model: FormModel): Promise<Statement>
    {
        return (await axios.put<Statement>(`admin/renters/properties/${publicId}`, model)).data;
    }

    /**
     * @param publicId string
     *
     * @returns Promise<Statement>
     */
    public static async remove(publicId: string): Promise<Statement>
    {
        return (await axios.delete(`admin/renters/properties/${publicId}`)).data as Statement;
    }

    /**
     * @param publicId string
     * @param equipment string[]
     *
     * @returns Promise<Statement>
     */
    public static async assignEquipment(publicId: string, equipment: string[]): Promise<Statement>
    {
        return (await axios.put<Statement>(`admin/renters/properties/${publicId}/assignEquipment`, equipment)).data;
    }

    /**
    * @param publicId string
    * @param noteValue string
    *
    * @returns Promise<Statement>
    */
    public static async setNote(publicId: string, noteValue: string): Promise<Statement>
    {
        return (await axios.put<Statement>(
            `admin/renters/properties/${publicId}/notes`,
            JSON.stringify(noteValue),
            { headers: { "Content-Type": "application/json" } }
        )).data;
    }

    /**
     * @param publicId string
     * @param details PropertyDetails
     *
     * @returns Promise<Statement>
     */
    public static async updateDetails(publicId: string, details: PropertyDetails): Promise<Statement>
    {
        return (await axios.put<Statement>(`admin/renters/properties/${publicId}/details`, details)).data;
    }

    /**
     * @param filter FilterModel
     * @param pager Pager
     *
     * @returns Promise<AxiosResponse>
     */
    public static async getRaport(filter: FilterModel, pager: Pager): Promise<AxiosResponse>
    {
        return (await axios.get<Blob>('admin/renters/properties/excel', {
            params: merge({}, filter, pager.data()),
            responseType: "blob"
        }));
    }

    /**
     *@param file File
     */
    public static async uploadExcel(file: FormData): Promise<Statement>
    {
        return (await axios.post('admin/renters/properties/import', file)).data;
    }

    /**
     * @param file File
     * @param container string
     * @param secured boolean
     *
     * @returns Promise<UploadResource>
     */
    public static async uploadMedia(file: File, container: string, payload: any = null, secured: boolean = false): Promise<UploadResource>
    {
        const data = fileNameConverter(file, container, payload, secured);

        const result = await axios.post<UploadResource>('admin/renters/properties/uploadFile', data);

        return result.data;
    }

    /**
     * @param data BulkUpdateModel
     *
     * @returns Promise<AxiosResponse>
     */
    public static async updateMultipleTasks(data: BulkUpdateModel ): Promise<AxiosResponse>
    {
        return (await axios.put<AxiosResponse>('admin/renters/properties/bulkOperation', data)).data;
    }


    /**
     * @param publicId string
     * @returns Promise<PropertyMediaSectionsResponse>
     */
    public static async fetchPropertyMediaSections(publicId: string): Promise<PropertyMediaSectionsResponse>
    {
        return (await axios.get<PropertyMediaSectionsResponse>(`admin/renters/files/property/${publicId}`)).data;
    }

    /**
     * @param file File
     * @returns Promise<UploadResource>
     */
    public static async uploadFileToStorage(file: File): Promise<UploadResource>
    {
        const formData = fileNameConverter(file, 'default', null, false);
        const uploadResult = await axios.post<UploadResource>('admin/renters/properties/uploadFile', formData);

        return uploadResult.data;
    }

    public static async uploadFileToProperty(
        publicId: string,
        file: File,
        attachmentType: 'Photo' | 'Video' | 'Pdf',
        sectionId?: number
    ): Promise<UploadResource>
    {
        const uploadResult = await this.uploadFileToStorage(file);

        const fileId = uploadResult?.id;

        if (sectionId && fileId)
        {
            const sectionsResponse = await this.fetchPropertyMediaSections(publicId);
            const section = sectionsResponse.result.sections.find(s => s.id === sectionId);

            if (section)
            {
                const updatedAttachmentsIds = [...(section.attachmentsIds || []), fileId];

                await this.updatePropertyMediaSection(section.publicId, {
                    room: String(section.room),
                    attachmentsType: section.attachmentsType || attachmentType,
                    attachmentsIds: updatedAttachmentsIds,
                    description: section.description
                });
            }
        }

        return uploadResult;
    }

    /**
     * @param publicId string
     * @param model CreatePropertyMediaSectionModel
     * @returns Promise<Resource<PropertyMediaSection>>
     */
    public static async createPropertyMediaSection(
        publicId: string,
        model: CreatePropertyMediaSectionModel
    ): Promise<Resource<PropertyMediaSection>>
    {
        const result = await axios.post<Resource<PropertyMediaSection>>(`admin/renters/files/property/${publicId}`, {
            room: model.room,
            description: model.description || null,
            attachmentsType: model.attachmentsType,
            attachmentsIds: model.attachmentsIds || []
        });

        return result.data;
    }

    /**
     * @param sectionPublicId string
     * @param model UpdatePropertyMediaSectionModel
     * @returns Promise<Statement>
     */
    public static async updatePropertyMediaSection(
        sectionPublicId: string,
        model: UpdatePropertyMediaSectionModel
    ): Promise<Statement>
    {
        if (!model.room)
        {
            throw new Error('Room is required');
        }

        if (!model.attachmentsType)
        {
            throw new Error('AttachmentsType is required');
        }

        const payload: any = {
            room: String(model.room),
            attachmentsType: String(model.attachmentsType),
            attachmentsIds: Array.isArray(model.attachmentsIds) ? model.attachmentsIds : [],
            description: model.description || null
        };

        return (await axios.put<Statement>(`admin/renters/files/property/sections/${sectionPublicId}`, payload)).data;
    }

    /**
     * @param sectionPublicId string
     * @returns Promise<Statement>
     */
    public static async deletePropertyMediaSection(sectionPublicId: string): Promise<Statement>
    {
        return (await axios.delete<Statement>(`admin/renters/files/property/sections/${sectionPublicId}`)).data;
    }

    /**
     * @param data MoveFilesBetweenSectionsModel
     * @returns Promise<Statement>
     */
    public static async moveFilesBetweenSections(data: MoveFilesBetweenSectionsModel): Promise<Statement>
    {
        return (await axios.put<Statement>('admin/renters/files/property/sections', data)).data;
    }

}

interface UserItemModel
{
    id: number;
    publicId: string;
    givenName: string;
    surname: string;
    email: string;
}

export interface ListItemModel
{
    id: number;
    publicId: string;
    propertyName: string;
    address: string;
    city: string;
    peopleCapacity: number;
    building: string;
    district?: string;
    isActive: boolean;
    openTasks: number;
    lastClean: DateTime;
    lastInspection: DateTime;
    ownerGivenName: string;
    ownerSurname: string;
    checkOutTime: DateTime;
    guestyCheckInTime?: GuestyTimeSpan;
    guestyCheckInEndTime?: GuestyTimeSpan;
    guestyCheckOutTime?: GuestyTimeSpan;
    masterCode: number;
}

export interface GuestyTimeSpan
{
    hour: number;
    minute: number;
    second?: number;
    millisecond?: number;
    ticks?: number;
}

export interface FilterModel
{
    search: string
    guestyId?: string;
    citiesIds: string[] | string;
    districtsIds: string[] | string;
    activeStatus: string;
    prestigeStatus: string;
    ownerName: string;
    hasNoDistrict: boolean
}

export interface VrosAttribute
{
    id: string;
    label: string;
    value: boolean;
    description?: string | null;
    category: string;
}

export interface VrosBuilding
{
    floor: string | null;
    hasElevator: boolean;
}

export interface VrosWifi
{
    hasRouter: boolean;
    networkName: string;
    username: string;
    password: string;
}

export interface VrosInstructions
{
    checkInInstructions: string;
    checkOutInstructions: string;
    guestGuideUrl: string;
    lockBoxCode: string;
}

export interface PropertyUtility
{
    provider: string | null;
    info: string | null;
}

export interface PropertyDetails
{
    comfort: Record<string, boolean>;
    equipment: Record<string, boolean>;
    safety: Record<string, boolean>;
    extras: Record<string, boolean>;
    utilities: Record<string, PropertyUtility>;
    inventory: string | null;
}

export interface VrosData
{
    attributes: VrosAttribute[];
    wifi: VrosWifi | null;
    instructions: VrosInstructions | null;
    building?: VrosBuilding | null;
    hasBalcony?: boolean;
    details?: PropertyDetails | null;
}

export interface FormModel
{
    id: number;
    publicId: string;
    isActive: boolean;
    accommodationMasterCode: number;
    propertyName: string;
    userKind: string;
    address: string;
    address2: string;
    country: string;
    state: string;
    county: string;
    city: string;
    cityId?: number;
    area: string;
    postalCode: string;
    streetType: string;
    streetName: string;
    streetNumber: string;
    typeStreetNumbering: string;
    defaultChecklistId?: number;
    defaultChecklistPublicId: string;
    bedrooms: number;
    toilets: number;
    bathroomWithBathtub: number;
    bathroomWithShower: number;
    individualBeds: number;
    doubleBeds: number;
    kingSizeBed: number;
    queenSizeBed: number;
    doubleSofaBed: number;
    berths: number;
    peopleCapacity: number;
    googleMapsLatitude: string;
    googleMapsLongitude: string;
    wifiNetworkName: string;
    wifiNetworkPassword: string;
    way: string;
    block: string;
    door: string;
    floor: string;
    keyNumber: string;
    staircase: string;
    propertyOwnerId?: number;
    ownerFullName?: string;
    tags: string[];
    documents: string[];
    photos: string[];
    videos: string[];
    pdfs: string[];
    marketingId: string;
    internalId: string;
    groupId: number;
    equipment: string[];
    propertyTimezone: string;
    building: string;
    additionalNotes: string;
    safeCodes: string;
    checkInTime: DateTime;
    checkOutTime: DateTime;
    guestyCheckInTime?: GuestyTimeSpan;
    guestyCheckInEndTime?: GuestyTimeSpan;
    guestyCheckOutTime?: GuestyTimeSpan;
    iCalLink: string;
    typeOfStructure: string;
    livingArea: number;
    yearBuilt: number;
    numberOfFloors: number;
    propertyNote: string;
    tagPublicId: string;
    districtPublicId: string;
    cleaningWage: number | string;
    smallSheetAmount: number;
    largeSheetAmount : number,
    brand: BrandsTypes;
    prestige: boolean;
    riskOfTermination: boolean;
    newContract: boolean;
    warehousemanNote: string
    isHidden: boolean;
    isHiddenForTasks: boolean;
    defaultCleaningUsers: UserItemModel[],
    defaultCleaningUserIds: number[],
    defaultInspectionUsers: UserItemModel[],
    defaultInspectionUserIds: number[],
    defaultMaintenanceUsers: UserItemModel[],
    defaultMaintenanceUserIds: number[],
    defaultCustomerServiceUsers: UserItemModel[],
    defaultCustomerServiceUserIds: number[],
    defaultOwnerServiceUsers: UserItemModel[],
    defaultOwnerServiceUserIds: number[],
    accessNote: string;
    canViewFiles?: boolean;
    vrosData?: VrosData | null;
}

interface UploadedResource
{
    upload?: File;
}

export interface UploadResource extends UploadedResource
{
    id: number;
    resourceType: string;
    dateCreatedUtc: DateTime;
    dateModifiedUtc: DateTime;
    createdBy: string;
    modifiedBy: string;
    scope: string;
    folderId?: number;
    name: string;
    contentType: string;
    contentLength: number;
    url: string;
    isImage: boolean;
    isSecured: boolean;
    meta: Record<string, string>;
    content: Record<string, any>;
    media: string[];
}

export interface BulkUpdateModel {
    districtId: number | null,
    defaultCleaningUsersIds: number[],
    defaultInspectionUsersIds: number[],
    defaultMaintenanceUsersIds: number[],
    defaultCustomerServiceUserIds: number[],
    defaultOwnerServiceUserIds: number[],
    propertiesIds: number[],
    clearDefaultUsers: boolean
}

export interface PropertyMedias {
    photos: string[];
    videos: string[];
    pdfs: string[];
}

export interface ModalType {
    ref: any;
    publicId: string;
}

export enum MediaType {
    Image = 'image',
    Video = 'video',
    Pdf = 'pdf',
}

enum StatusEnum {
    Dirty = 'Dirty',
    InProgress = 'InProgress',
    Clean = 'Clean',
    Occupied = 'Occupied'
}

const StatusOptions = (): Option[] => ([
    { value: StatusEnum.Dirty, text: $t('[[[Brudne]]]') },
    { value: StatusEnum.InProgress, text: $t('[[[W trakcie]]]') },
    { value: StatusEnum.Clean, text: $t('[[[Posprzątane]]]') },
    { value: StatusEnum.Occupied, text: $t('[[[Zajęte]]]') }
]);

const StatusName = (value: StatusEnum): string => StatusOptions().find(s => s.value === value)?.text || StatusEnum[value];

enum ActiveEnum {
    Nonactive = 'Nonactive',
    Active = 'Active',
    All = 'All'
}

const ActiveOptions = (): Option[] => ([
    { value: ActiveEnum.Nonactive, text: $t('[[[Nieaktywne]]]') },
    { value: ActiveEnum.Active, text: $t('[[[Aktywne]]]') },
    { value: ActiveEnum.All, text: $t('[[[Wszystkie]]]') }
]);

enum PrestigeEnum {
    Nonprestige = 'Nonprestige',
    Prestige = 'Prestige',
    All = 'All'
}

const PrestigeOptions = (): Option[] => ([
    { value: PrestigeEnum.Nonprestige, text: $t('[[[Podstawowe]]]') },
    { value: PrestigeEnum.Prestige, text: $t('[[[Prestiżowe]]]') },
    { value: PrestigeEnum.All, text: $t('[[[Wszystkie]]]') }
]);

const ActiveName = (value: ActiveEnum): string => ActiveOptions().find(a => a.value === value)?.text || ActiveEnum[value];
const PrestigeName = (value: PrestigeEnum): string => PrestigeOptions().find(p => p.value === value)?.text || PrestigeEnum[value];

export {
    StatusEnum,
    StatusOptions,
    StatusName,
    ActiveEnum,
    ActiveOptions,
    ActiveName,
    PrestigeEnum,
    PrestigeOptions,
    PrestigeName
};

export interface PropertiesItemModel extends FormModel {}

export enum PropertyRoom {
    Other = 'Other',
    Building = 'Building',
    LivingRoom = 'LivingRoom',
    Bedroom = 'Bedroom',
    Kitchen = 'Kitchen',
    Bathroom = 'Bathroom',
    Hallway = 'Hallway'
}

export const PropertyRoomNames: Record<PropertyRoom, string> = {
    [PropertyRoom.Other]: '[[[Inne]]]',
    [PropertyRoom.Building]: '[[[Budynek]]]',
    [PropertyRoom.LivingRoom]: '[[[Pokój dzienny]]]',
    [PropertyRoom.Bedroom]: '[[[Sypialnia]]]',
    [PropertyRoom.Kitchen]: '[[[Kuchnia]]]',
    [PropertyRoom.Bathroom]: '[[[Łazienka]]]',
    [PropertyRoom.Hallway]: '[[[Korytarz]]]',
};

export enum AttachmentType {
    Photo = 'Photo',
    Video = 'Video',
    Pdf = 'Pdf'
}

export const AttachmentTypeNames: Record<AttachmentType, string> = {
    [AttachmentType.Photo]: '[[[Zdjęcia]]]',
    [AttachmentType.Video]: '[[[Filmy]]]',
    [AttachmentType.Pdf]: '[[[PDF]]]',
};

export interface PropertyMediaAttachment {
    createdByUserId: number;
    id: number;
    publicId: string;
    resourceType: string;
    dateCreatedUtc: DateTime;
    dateModifiedUtc?: DateTime;
    createdBy: string;
    modifiedBy?: string;
    scope: string;
    folderId?: number;
    name: string;
    contentType: string;
    contentLength: number;
    url: string;
    isSecured: boolean;
    isImage: boolean;
    meta: Record<string, string>;
    content: Record<string, any>;
    media: string[];
}

export interface PropertyMediaSection {
    id: number;
    publicId: string;
    room: PropertyRoom | string;
    description?: string;
    attachmentsType?: 'Photo' | 'Video' | 'Pdf' | string;
    attachmentsIds: number[];
    attachments: PropertyMediaAttachment[];
}

export interface PropertyMediaSectionsResponse {
    result: {
        propertyId: number;
        sections: PropertyMediaSection[];
    };
    meta?: any;
    code?: number;
    message?: string;
}

export interface CreatePropertyMediaSectionModel {
    room: PropertyRoom;
    description?: string;
    attachmentsType: 'Photo' | 'Video' | 'Pdf';
    attachmentsIds?: number[];
}

export interface UpdatePropertyMediaSectionModel {
    room: string;
    attachmentsType: 'Photo' | 'Video' | 'Pdf' | string;
    description?: string;
    attachmentsIds?: number[];
}

export interface MoveFilesBetweenSectionsModel {
    sections: Array<{
        id: number;
        attachmentsIds: number[];
    }>;
}
